import {
  CheckOutlined,
  FileSearchOutlined,
  StarFilled,
} from '@ant-design/icons';
import { Button, Modal, Progress } from 'antd';
import { useTranslation } from 'react-i18next';

import { OperationRequestReceipt } from 'models/OperationRequest';

import { formatClp } from '../data/operationCatalogs';

import './OpsReceiptModal.scss';

export type OpsReceiptModalMode = 'preview' | 'success';

interface OpsReceiptModalProps {
  receipt: OperationRequestReceipt | null;
  mode: OpsReceiptModalMode | null;
  submitting?: boolean;
  variant?: 'create' | 'edit';
  onClose: () => void;
  onConfirm: () => void;
  onDone: () => void;
}

function OpsReceiptModal({
  receipt,
  mode,
  submitting,
  variant = 'create',
  onClose,
  onConfirm,
  onDone,
}: OpsReceiptModalProps) {
  const { t } = useTranslation();
  const i18nBase =
    'scenes.requirements_operation.components.new_operation_request.receipt';
  const isPreview = mode === 'preview';
  const isEdit = variant === 'edit';
  const open = !!receipt && !!mode;

  const titleKey = isPreview
    ? isEdit
      ? 'edit_preview_title'
      : 'preview_title'
    : isEdit
    ? 'edit_title'
    : 'title';
  const descriptionKey = isPreview
    ? isEdit
      ? 'edit_preview_description'
      : 'preview_description'
    : isEdit
    ? 'edit_description'
    : 'description';

  return (
    <Modal
      open={open}
      title={null}
      footer={null}
      onCancel={onClose}
      destroyOnClose
      width={560}
      className="ops-receipt-modal"
      centered
      maskClosable={!submitting}
      closable={!submitting}
      styles={{
        body: { padding: 0, maxHeight: '85vh', overflow: 'hidden' },
      }}
    >
      {receipt && mode && (
        <div className="ops-receipt-modal__shell">
          <header className="ops-receipt-modal__head">
            <span
              className={`ops-receipt-modal__check${
                isPreview ? ' ops-receipt-modal__check--preview' : ''
              }`}
              aria-hidden
            >
              {isPreview ? <FileSearchOutlined /> : <CheckOutlined />}
            </span>
            <div className="ops-receipt-modal__head-text">
              <h2 className="ops-receipt-modal__title">
                {t(`${i18nBase}.${titleKey}`)}
              </h2>
              <p className="ops-receipt-modal__description">
                {t(`${i18nBase}.${descriptionKey}`)}
              </p>
            </div>
          </header>

          <div className="ops-receipt-modal__scroll">
            <dl className="ops-receipt-modal__grid">
              <div className="ops-receipt-modal__row">
                <dt>{t(`${i18nBase}.labels.id`)}</dt>
                <dd className="ops-receipt-modal__id">
                  {isPreview && !isEdit
                    ? t(`${i18nBase}.id_pending`)
                    : receipt.id}
                </dd>
              </div>
              <div className="ops-receipt-modal__row">
                <dt>{t(`${i18nBase}.labels.client`)}</dt>
                <dd>{receipt.brand}</dd>
              </div>
              <div className="ops-receipt-modal__row">
                <dt>{t(`${i18nBase}.labels.campaign`)}</dt>
                <dd>{receipt.campaign}</dd>
              </div>
              <div className="ops-receipt-modal__row">
                <dt>{t(`${i18nBase}.labels.type`)}</dt>
                <dd>
                  {t(
                    `scenes.requirements_operation.scene.contract.${receipt.type}`
                  )}
                </dd>
              </div>
              <div className="ops-receipt-modal__row">
                <dt>{t(`${i18nBase}.labels.week`)}</dt>
                <dd>{receipt.week}</dd>
              </div>
              <div className="ops-receipt-modal__row">
                <dt>{t(`${i18nBase}.labels.profile`)}</dt>
                <dd>{receipt.profile}</dd>
              </div>
              <div className="ops-receipt-modal__row">
                <dt>{t(`${i18nBase}.labels.turns`)}</dt>
                <dd>
                  {t(`${i18nBase}.turns_slots`, {
                    turns: receipt.turnos,
                    slots: receipt.slots,
                  })}
                </dd>
              </div>
              <div className="ops-receipt-modal__row">
                <dt>{t(`${i18nBase}.labels.stores`)}</dt>
                <dd className="ops-receipt-modal__stores">
                  <span>
                    {t(`${i18nBase}.stores_count`, { count: receipt.stores })}
                  </span>
                  {receipt.vipStores > 0 && (
                    <span className="ops-receipt-modal__vip">
                      <StarFilled /> {receipt.vipStores} VIP
                    </span>
                  )}
                </dd>
              </div>
              <div className="ops-receipt-modal__row">
                <dt>{t(`${i18nBase}.labels.cost`)}</dt>
                <dd className="ops-receipt-modal__cost">
                  {formatClp(receipt.cost)} {t(`${i18nBase}.cost_suffix`)}
                </dd>
              </div>
              <div className="ops-receipt-modal__row">
                <dt>{t(`${i18nBase}.labels.kam`)}</dt>
                <dd>{receipt.kam}</dd>
              </div>
              <div className="ops-receipt-modal__row">
                <dt>{t(`${i18nBase}.labels.supervisor`)}</dt>
                <dd className="ops-receipt-modal__muted">
                  {t(`${i18nBase}.supervisor_pending`)}
                </dd>
              </div>
              <div className="ops-receipt-modal__row ops-receipt-modal__row--assign">
                <dt>{t(`${i18nBase}.labels.assignment`)}</dt>
                <dd>
                  <div className="ops-receipt-modal__assign-text">
                    <strong>0%</strong> 0/{receipt.slots}{' '}
                    {t(`${i18nBase}.assigned`)}
                  </div>
                  <Progress
                    percent={0}
                    showInfo={false}
                    size="small"
                    className="ops-receipt-modal__progress"
                  />
                </dd>
              </div>
            </dl>
          </div>

          <footer className="ops-receipt-modal__actions">
            <Button disabled={submitting} onClick={onClose}>
              {t(`${i18nBase}.buttons.close`)}
            </Button>
            {isPreview ? (
              <Button
                type="primary"
                className="ops-receipt-modal__primary"
                loading={submitting}
                onClick={onConfirm}
              >
                {t(`${i18nBase}.buttons.${isEdit ? 'save_changes' : 'save'}`)}
              </Button>
            ) : (
              <Button
                type="primary"
                className="ops-receipt-modal__primary"
                onClick={onDone}
              >
                {t(`${i18nBase}.buttons.done`)}
              </Button>
            )}
          </footer>
        </div>
      )}
    </Modal>
  );
}

export default OpsReceiptModal;
